import React, { useState } from "react";
import ItemDetailsTooltip from "~/widgets/ItemDetailsTooltip/ItemDetailsTooltip";
import type { CharacterData } from "~/types/storage";
import type { TagAction } from "~/pages/CharacterSheet/types";
import styles from "~/pages/GmView/widgets/CompactItemsList/CompactItemsList.scss";

export interface CompactItem {
  id: string;
  content: string;
}

export interface CompactItemsListProps {
  items: CompactItem[];
  /**
   * Maximum number of items to display before showing "+N more"
   * @default 3
   */
  limit?: number;
  /**
   * Character data for action tag context (optional - tags won't be interactive without this)
   */
  characterData?: CharacterData | null;
  /**
   * Callback when item content is updated
   */
  onUpdateItem?: (id: string, content: string) => void;
  /**
   * Callback when item is deleted
   */
  onDeleteItem?: (id: string) => void;
  /**
   * Callback for tag actions (heal, damage, etc.)
   */
  onTagAction?: (action: TagAction) => void;
  /**
   * Callback for roll actions
   */
  onRoll?: (name: string, pool: number, flatHits?: number) => void;
}

interface ParsedItem {
  id: string;
  name: string;
  type: "Item" | "Ability" | null;
  tags: string[];
  fullContent: string;
}

/**
 * Parses item content to extract name, type, and action tags.
 */
function parseItemContent(item: CompactItem): ParsedItem {
  const lines = item.content.split('\n');
  const firstLine = lines[0] || "";

  const isItem = firstLine.trim().match(/^\[Item\]/i);
  const isAbility = firstLine.trim().match(/^\[Ability\]/i);

  let titleText = firstLine
    .replace(/^\s*\[Item\]/i, "")
    .replace(/^\s*\[Ability\]/i, "")
    .trim();

  // Extract tags like [+3 Health], [-1 Strain], etc.
  const tagRegex = /\[([^\]]+)\]/g;
  const tags: string[] = [];
  let match;
  while ((match = tagRegex.exec(item.content)) !== null) {
    // Skip the [Item] and [Ability] tags
    if (!match[1].match(/^(Item|Ability)$/i)) {
      tags.push(match[1]);
    }
  }

  // Clean title - remove any remaining tags
  const name = titleText.replace(/\[[^\]]+\]/g, "").trim() || (isItem ? "Item" : isAbility ? "Ability" : "Unknown");

  return {
    id: item.id,
    name,
    type: isItem ? "Item" : isAbility ? "Ability" : null,
    tags,
    fullContent: item.content,
  };
}

/**
 * CompactItemsList Component
 *
 * Displays items/abilities in a compact format showing:
 * - Item/Ability name
 * - Action tags (e.g., [+3 Health], [-1 Strain])
 * - "+N more" indicator when items exceed the limit
 * - Click any item to open full ItemCard in a popup (with editing support)
 */
const CompactItemsList: React.FC<CompactItemsListProps> = ({
  items,
  limit = 3,
  characterData,
  onUpdateItem,
  onDeleteItem,
  onTagAction,
  onRoll,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  // Character actions for context
  const characterActions = {
    onRoll: onRoll || (() => { }),
    onTagAction: onTagAction || (() => { }),
  };

  const parsedItems = items.map(parseItemContent);
  const visibleItems = parsedItems.slice(0, limit);
  const hiddenCount = Math.max(0, parsedItems.length - limit);

  const handleItemClick = (itemId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorRect(rect);
    setSelectedItemId(itemId);
    setEditingItemId(null);
  };

  const closeTooltip = () => {
    setSelectedItemId(null);
    setEditingItemId(null);
    setAnchorRect(null);
  };

  const selectedItem = items.find(i => i.id === selectedItemId);

  const handleToggleEdit = (id: string) => {
    setEditingItemId(prev => prev === id ? null : id);
  };

  if (items.length === 0) {
    return (
      <div className={styles.emptyList}>
        No items
      </div>
    );
  }

  return (
    <>
      <div className={styles.itemsList}>
        {visibleItems.map((item) => (
          <button
            key={item.id}
            className={styles.itemRow}
            onClick={(e) => handleItemClick(item.id, e)}
            type="button"
          >
            <span className={styles.itemName}>
              {item.type && (
                <span className={item.type === "Item" ? styles.itemBadge : styles.abilityBadge}>
                  {item.type[0]}
                </span>
              )}
              {item.name}
            </span>
            {item.tags.length > 0 && (
              <span className={styles.tagsList}>
                {item.tags.slice(0, 2).map((tag, idx) => (
                  <span key={idx} className={styles.tag}>
                    {tag}
                  </span>
                ))}
                {item.tags.length > 2 && (
                  <span className={styles.tagMore}>+{item.tags.length - 2}</span>
                )}
              </span>
            )}
          </button>
        ))}
        {hiddenCount > 0 && (
          <div className={styles.moreIndicator}>
            +{hiddenCount} more
          </div>
        )}
      </div>

      <ItemDetailsTooltip
        item={selectedItem || null}
        isEditing={editingItemId === selectedItemId}
        anchorRect={anchorRect}
        onClose={closeTooltip}
        onToggleEdit={handleToggleEdit}
        onUpdate={onUpdateItem}
        onDelete={onDeleteItem}
        characterData={characterData}
        actions={characterActions}
      />
    </>
  );
};

export default CompactItemsList;
